import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  createChart,
  deleteChart,
  getChartById,
  getChartSourceData,
  getPageCharts,
  setChart,
} from "../api/chart";
import { fetchFullTable } from "../api/table";
import { useAppId } from "../hook";
import { ActionType, RootState } from "../reducer";
import { AddChartsAction } from "../reducer/chart";
import { AddFullTablesAction } from "../reducer/table";
import { Chart, ChartSourceData } from "../types/chart";
import { ChartBlock } from "../types/notebook/note/block/chart";
import { NotePageData } from "../types/note";
import { FullTableMap, Table, TableFile } from "../types/table";
import { coerceTableFileNumbers } from "../util/table";
import { useFetchNotebookPage, useSelectedNotebookPageId } from "./page";
import { useTableFromFileId } from "./table";

export const useAddCharts: () => (charts: Chart[]) => void = () => {
  const dispatch = useDispatch();
  return (charts) => {
    const action: AddChartsAction = {
      charts,
      type: ActionType.ADD_CHARTS,
    };
    dispatch(action as any);
  };
};

export const useChart: (chartId: string) => Chart | undefined = (chartId) => {
  const chart = useSelector((state: RootState) => {
    return state.ui.charts[chartId];
  });
  return chart;
};

export const useFetchPageCharts: () => (config: {
  pageId: string;
}) => Promise<Chart[]> = () => {
  const [appId] = useAppId();
  const addCharts = useAddCharts();

  return ({ pageId }) => {
    if (!appId) {
      throw new Error("No app Id!");
    }

    return getPageCharts({ appId, pageId }).then((res) => {
      addCharts(res);
      return res;
    });
  };
};

export const useSetChart: () => (config: {
  chartId: string;
  chartData: Chart;
}) => Promise<any> = () => {
  const [appId] = useAppId();
  const [pageId] = useSelectedNotebookPageId();
  const fetchPageCharts = useFetchPageCharts();

  return ({ chartId, chartData }) => {
    if (!appId) {
      throw new Error("No App Id!");
    }
    if (!pageId) {
      throw new Error("No Page Id!");
    }

    return setChart({ appId, chartId, chartData }).then(() => {
      return fetchPageCharts({ pageId });
    });
  };
};

export const useFetchPageChartInputTables: () => (config: {
  note: NotePageData;
  tables: Table[];
  pageId: string;
}) => Promise<TableFile[]> = () => {
  const fetchPageCharts = useFetchPageCharts();
  const [appId] = useAppId();
  const dispatch = useDispatch();

  return async ({ note, tables, pageId }) => {
    if (!appId) {
      throw new Error("No App Id!");
    }

    const charts = await fetchPageCharts({ pageId });
    const chartFileIds = charts.flatMap((chart) => chart.inputs);
    const fullTablesOrMissing = await Promise.all(
      chartFileIds.map((fileId) =>
        fetchFullTable({ appId, fileId }).catch((e) => {
          console.error("FAILED_TO_FETCH_CHART_INPUT_TABLE", fileId);
          return undefined;
        }),
      ),
    );
    const fullTables = fullTablesOrMissing.filter(
      (v) => v !== undefined,
    ) as TableFile[];
    // console.log("ADD_FULL_TABLES", tables);
    const action: AddFullTablesAction = {
      type: ActionType.ADD_FULL_TABLES,
      tables: fullTables,
    };
    dispatch(action as any);
    return fullTables;
  };
};

export const useChartInputTables: (chartId: string) => FullTableMap = (
  chartId,
) => {
  const allFullTables = useSelector(
    (state: RootState) => state.tables.fullTables,
  );
  const tableFromfileId = useTableFromFileId();
  const chart = useChart(chartId);
  const inputFileIds = chart?.inputs ?? [];
  const chartInputTableIds = inputFileIds
    .map((fileId) => tableFromfileId(fileId))
    .map((tableOrNotFound) => tableOrNotFound["file-id"])
    .filter((fileId) => fileId) as string[];
  const fullTableEntries = chartInputTableIds
    .map((fileId) => [fileId, allFullTables[fileId]] as [string, TableFile])
    .filter(([tableId, table]) => table)
    .map(([tableId, table]) => [tableId, coerceTableFileNumbers(table)]);
  const fullTables = Object.fromEntries(fullTableEntries);
  return fullTables;
};

/**
 * Fetch-listener (kb query-omega/frontend/block-type-hook-pattern.html): load the block's Chart
 * Record on mount so state.ui.charts[chartId] is filled — nothing else ever loaded it, so a chart
 * block rendered an empty shell.
 */
export const useFetchChartListener = (block: ChartBlock | undefined) => {
  const addCharts = useAddCharts();
  useEffect(() => {
    if (!block || !block["app-id"] || !block["page-id"] || !block["block-id"]) return;
    getChartById({
      "app-id": block["app-id"],
      "page-id": block["page-id"],
      "block-id": block["block-id"],
    }).then((chart) => {
      if (chart) addCharts([chart]);
    });
  }, [block?.["app-id"], block?.["page-id"], block?.["block-id"]]);
};

/**
 * The Chart Source's answer for a chart that has one (chart.source["page-id"]), else undefined.
 * Kept in component state: it is derived, per-render data, not app state.
 */
export const useChartSourceData = (
  block: ChartBlock | undefined,
  chart: Chart | undefined,
  inputValues: Record<string, unknown> = {},
): ChartSourceData | undefined => {
  const [data, setData] = useState<ChartSourceData | undefined>(undefined);
  const sourcePageId = chart?.source?.["page-id"];
  const sourceInput = JSON.stringify(chart?.source?.input ?? {});
  // Input Values (chart-inputs.tsx): a change re-runs the Chart Source with them; keyed by value, not identity.
  const values = JSON.stringify(inputValues);
  useEffect(() => {
    if (!block || !sourcePageId) return;
    getChartSourceData({
      "app-id": block["app-id"],
      "page-id": block["page-id"],
      "block-id": block["block-id"],
      inputValues: JSON.parse(values),
    })
      .then((d) => setData(d))
      .catch((e) => console.error("FAILED_TO_FETCH_CHART_SOURCE", e));
  }, [block?.["app-id"], block?.["page-id"], block?.["block-id"], sourcePageId, sourceInput, values]);
  return data;
};

export const useChartOptionECharts: (
  chartId: string,
  sourceData?: ChartSourceData,
) => any = (chartId, sourceData) => {
  const chart = useChart(chartId)!;
  const inputTables = useChartInputTables(chartId);
  const inputDatasets = Object.values(inputTables).map((table) => ({
    id: table["file-id"],
    source: [table.header, ...table.rows],
  }));
  const sourceDatasets = sourceData
    ? [{ id: "source", source: [sourceData.header, ...sourceData.rows] }]
    : [];
  try {
    const codeJson = JSON.parse(chart.code);
    const option = { ...codeJson, dataset: [...sourceDatasets, ...inputDatasets] };
    return option;
  } catch (e) {
    if (e instanceof Error) {
      return { error: true, message: e.message };
    }
    return { error: true, message: String(e) };
  }
};

export const useInsertChartBlock: () => (config: {
  blockIndex: number;
}) => Promise<any> = () => {
  const [pageId] = useSelectedNotebookPageId();
  const [appId] = useAppId();
  const fetchNotebookPage = useFetchNotebookPage(pageId);

  return async ({ blockIndex }) => {
    if (!pageId) {
      throw new Error("No Page Id!");
    }
    if (!appId) {
      throw new Error("No App Id!");
    }

    const initChart: Chart = {
      "app-id": appId,
      "page-id": pageId,
      "block-index": blockIndex,
      code: "{}",
      inputs: [],
      name: "New Chart",
      description: "New Chart",
    };
    return createChart(initChart).then(() => {
      return fetchNotebookPage();
    });
  };
};

export const useDeleteChart: () => (config: {
  chartId: string;
  pageId: string;
}) => Promise<any> = () => {
  const [appId] = useAppId();

  return async ({ pageId, chartId }) => {
    if (!appId) {
      throw new Error("No app Id!");
    }

    deleteChart({
      appId,
      chartId,
      pageId,
    });
  };
};
